import { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Button,
  TextBox,
  ValidationProvider,
  useValidation,
} from "@etframework/ui.components";
import { useTranslation } from "react-i18next";
import useModalEvent from "../../hooks/useModalEvent";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { useNavigate } from "react-router-dom";
import { useCreateView } from "../../data/mutations/viewMutations";
import { useQueryClient } from "@tanstack/react-query";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";

export const CreateViewModal = () => {
  return (
    <ValidationProvider>
      <InnerCreateViewModal />
    </ValidationProvider>
  );
};

const InnerCreateViewModal = () => {
  const { currentBoardId } = useBoardProvider();
  const { UserId } = useDueDatesAuth();
  const createViewBtnRef = useRef(null);
  const { setValidationErrors, clearValidationErrors } = useValidation();
  const { t } = useTranslation(["CreateViewModal"], {
    useSuspense: false,
  });
  const nameRef = useRef();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  //Modal
  const modalRef = useRef(null);
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const id = ModalEvents.CreateViewModal.Id;
  const ids = {
    name: id + "_name-input",
    createButton: id + "_create-view-button",
  };

  const [persistentData, setPersistentData] = useState({});

  const pathPostfix = currentBoardId ? `?boardId=${currentBoardId}` : "";
  const { mutate: createView, isPending } = useCreateView();

  const onModelBeforeClose = () => {};

  const closeModal = useCallback(() => {
    setPersistentData({});
    clearValidationErrors();
    setShowModal(false);
  }, []);

  const openModal = useCallback(() => {
    setShowModal(true);
  }, []);

  const toggleModal = () => {
    if (showModal) {
      closeModal();
    } else {
      openModal();
    }
  };

  const ModalContent = () => {
    const onCreateView = async () => {
      const view = {
        BoardId: currentBoardId,
        UserId: UserId,
        Name: nameRef.current.getValue(),
        IsFavorite: false,
      };

      setPersistentData({
        view,
      });

      createView(
        { view },
        {
          onSuccess: (data) => {
            const createdViewId = data.Entity?.Id;
            queryClient.invalidateQueries(["get-views"]);
            toast.success(t("SuccessMessage"), {
              position: "bottom-left",
            });
            setTimeout(() => {
              navigate(`/duedates/views/${createdViewId}${pathPostfix}`);
              closeModal();
            }, 500);
          },
          onError: (errors) => {
            console.error(errors);
            setValidationErrors(errors?.data);
          },
        }
      );
    };

    useEffect(() => {
      const handleKeyPress = (e) => {
        if (e.key === "Enter" && modalRef.current?.visible) {
          createViewBtnRef.current?.getComponentRef()?.click();
        }
      };

      document.addEventListener("keydown", handleKeyPress);

      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }, []);

    return (
      <div className="et-create-view-modal">
        <div>
          <span className="create-view-header d-block">
            {t("CreateViewTitle")}
          </span>
          <span className="mb-3 d-block">{t("CreateViewSubtitle")}</span>
          <div className="row">
            <div className="col">
              <TextBox
                ref={nameRef}
                name="Name"
                id={ids.name}
                value={persistentData?.Name}
                type="text"
                labelText={t("Name")}
                showClearButton
                className="font-awesome-input-placeholder"
              />
            </div>
          </div>
          <div className="d-flex justify-content-end create-view-modal-btns mx-auto gap-3">
            <Button
              text={t("CancelBtn")}
              className="btn-tertiary"
              onClick={closeModal}
            />
            <Button
              ref={createViewBtnRef}
              id={ids.createButton}
              text={t("CreateBtn")}
              className="btn btn-primary"
              onClick={onCreateView}
              loading={isPending}
            />
          </div>
        </div>
      </div>
    );
  };

  useRegisterEvent(ModalEvents.CreateViewModal, toggleModal);

  return ReactDOM.createPortal(
    <Modal
      id={id}
      ref={modalRef}
      isModal
      showCloseIcon={true}
      visible={showModal}
      content={<ModalContent />}
      close={closeModal}
      beforeClose={onModelBeforeClose}
      minHeight="30%"
      width="500px"
    ></Modal>,
    document.body
  );
};
